import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Copy, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/apiService';
import AjudaConciliacao from '../components/AjudaConciliacao';
import SituacaoBaixaErp, { rotulosBaixa } from '../components/SituacaoBaixaErp';

const moeda = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const valorErp = v => v == null ? 'Indisponível' : moeda(v);
const data = v => v ? new Date(v).toLocaleString('pt-BR') : 'Não informada';

export default function BaixaPix() {
  const [links, setLinks] = useState([]);
  const [filtro, setFiltro] = useState('pendentes');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const versao = useRef(0);
  const carregar = useCallback(async () => {
    const atual = ++versao.current;
    setCarregando(true);
    try {
      const resposta = await apiService.baixaPix.listar();
      if (atual !== versao.current) return;
      setLinks(resposta.data.data);
      setErro('');
    } catch (e) {
      if (atual === versao.current) {
        setLinks([]);
        setErro(e.response?.data?.message || 'Não foi possível consultar as situações.');
      }
    } finally { if (atual === versao.current) setCarregando(false); }
  }, []);
  useEffect(() => {
    let ativo = true;
    let timer;
    async function atualizar() {
      await carregar();
      if (ativo) timer = setTimeout(atualizar, 30000);
    }
    atualizar();
    return () => { ativo = false; clearTimeout(timer); versao.current++; };
  }, [carregar]);
  async function copiar(codigo) {
    try { await navigator.clipboard.writeText(String(codigo)); toast.success('Código copiado.'); }
    catch { toast.error('Selecione o código e copie manualmente.'); }
  }
  const visiveis = links.filter(l => filtro === 'todos' || (filtro === 'erro_associacao' ? l.erro_associacao : (filtro === 'pendentes' ? l.situacao_erp !== 'baixado' : l.situacao_erp === filtro)));
  const semBaixa = links.filter(l => ['aguardando', 'parcial'].includes(l.situacao_erp)).length;
  const errosAssociacao = links.filter(l => l.erro_associacao).length;
  const naoVerificados = links.filter(l => l.situacao_erp === 'nao_verificado').length;
  return <div className="space-y-4">
    <div className="flex items-center gap-2 text-gray-800">
      <h2 className="text-lg font-semibold">Pagamentos PIX e situação no ERP</h2>
      <AjudaConciliacao />
    </div>
    {!erro && semBaixa > 0 && <div role="status" className="border border-orange-300 bg-orange-50 p-4 rounded-lg text-orange-900">
      <strong>Atenção do caixa: {semBaixa} {semBaixa === 1 ? 'pagamento com parcela ainda aberta' : 'pagamentos com parcelas ainda abertas'} no ERP.</strong>
      <p className="text-sm mt-1">Nos pagamentos carregados nesta aba, o PIX consta como pago e a consulta encontrou parcelas vinculadas ainda abertas. Confira e regularize a baixa no ERP.</p>
    </div>}
    {!erro && errosAssociacao > 0 && <div role="alert" className="border border-red-300 bg-red-50 p-4 rounded-lg text-red-800">
      <strong>{errosAssociacao} {errosAssociacao === 1 ? 'pagamento com erro de associação identificado' : 'pagamentos com erro de associação identificado'}.</strong>
      <p className="text-sm mt-1">O vínculo diverge dos dados encontrados no ERP. Confira o motivo em cada linha e corrija a associação na aba Identificação e vínculos antes de usar essas referências para baixa. Isso não significa falta de pagamento.</p>
    </div>}
    {!erro && naoVerificados > 0 && <p role="status" className="border border-gray-300 bg-gray-50 p-3 rounded-lg text-sm text-gray-700">
      {naoVerificados} {naoVerificados === 1 ? 'pagamento exige' : 'pagamentos exigem'} conferência: não foi possível confirmar a situação no ERP. Esses casos não são contabilizados no alerta de parcelas abertas.
    </p>}

    <div className="flex flex-wrap gap-3 items-center justify-between">
      <label className="text-sm">Exibir <select value={filtro} onChange={e => setFiltro(e.target.value)} className="ml-2 border rounded-lg p-2">
        <option value="pendentes">Pendentes / conferir / vendas</option>
        {Object.entries(rotulosBaixa).map(([valor, rotulo]) => <option key={valor} value={valor}>{rotulo}</option>)}
        <option value="erro_associacao">Erros de associação identificados</option>
        <option value="todos">Todos</option>
      </select></label>
      <button onClick={carregar} disabled={carregando} className="flex gap-2 items-center border rounded-lg px-4 py-2 disabled:opacity-50"><RefreshCw size={16} />{carregando ? 'Consultando…' : 'Atualizar situação'}</button>
    </div>
    {!erro && <p className="text-sm text-gray-600">Exibindo {visiveis.length} de {links.length} pagamentos carregados. O filtro pode ocultar parcelas já baixadas.</p>}
    {erro && <p role="alert" className="bg-red-50 text-red-800 p-3 rounded-lg">{erro}</p>}
    <div className="bg-white border rounded-lg overflow-x-auto"><table className="w-full text-sm text-left">
      <thead className="bg-gray-50"><tr>{['Código', 'Cliente', 'Parcela / Venda', 'PIX pago', 'Valor previsto no ERP', 'Saldo em aberto no ERP', 'Diferença (PIX − ERP)', 'Juros', 'Pagamento PIX', 'Situação no ERP'].map(t => <th className="p-3 whitespace-nowrap" key={t}>{t}</th>)}</tr></thead>
      <tbody>{visiveis.map(l => <tr key={l.id} className={`border-t align-top ${l.erro_associacao ? 'bg-red-50' : ''}`}>
        <td className="p-3"><div className="flex gap-2"><span className="select-all">{l.codigo_cliente}</span><button onClick={() => copiar(l.codigo_cliente)} aria-label={`Copiar código ${l.codigo_cliente}`}><Copy size={16} /></button></div></td>
        <td className="p-3">{l.nome_cliente}<p className="text-xs text-gray-500">PIX #{l.id}</p></td>
        <td className="p-3 min-w-64">{l.tipo_pix === 'venda_avista' ? `Venda à vista • sequência ${l.sequencia_venda}` : `Ordens das parcelas no ERP: ${l.ordens_contas.join(', ')}`}
          <p className="text-gray-500">{l.descricao}</p>
          {l.venda_erp && <p className="text-xs text-gray-500">Movimento #{l.venda_erp.ordem} • Valor da venda no ERP: {moeda(l.venda_erp.valor)}</p>}
          {l.parcelas_erp?.map(p => <div key={p.ordem_conta} className="mt-2 text-xs border-t pt-1">
            <p>Conta {p.ordem_conta}: {p.descricao}</p><p>{p.motivo}</p>
            {p.valor_total != null && <p>Total: {moeda(p.valor_total)} • Quitado: {moeda(p.valor_quitado)}{p.data_quitacao ? ` • Quitação: ${data(p.data_quitacao)}` : ''}</p>}
          </div>)}
        </td>
        <td className="p-3 whitespace-nowrap">{moeda(l.valor_pago)}</td>
        <td className="p-3 whitespace-nowrap">{valorErp(l.valor_previsto_erp)}</td>
        <td className="p-3 whitespace-nowrap">{valorErp(l.saldo_aberto_erp)}</td>
        <td className={`p-3 whitespace-nowrap ${l.diferenca_valor_erp == null || l.diferenca_valor_erp === 0 ? 'text-gray-700' : 'text-amber-800 font-semibold'}`}>
          {valorErp(l.diferenca_valor_erp)}
          {l.diferenca_valor_erp != null && l.diferenca_valor_erp !== 0 && <p className="text-xs font-normal">{l.diferenca_valor_erp > 0 ? 'PIX acima do previsto' : 'PIX abaixo do previsto'}</p>}
        </td>
        <td className="p-3 whitespace-nowrap">{moeda(l.valor_juros)}</td>
        <td className="p-3">{data(l.data_pagamento)}</td><td className="p-3"><SituacaoBaixaErp dados={l} /></td>
      </tr>)}</tbody>
    </table>{!visiveis.length && <p className="p-8 text-center text-gray-500">{carregando ? 'Consultando…' : erro ? 'Consulta indisponível.' : 'Nenhum pagamento neste filtro.'}</p>}</div>
  </div>;
}
